import { create } from "zustand";

/**
 * The dashboard's theme: the user's choice (light, system or dark), saved to localStorage as a
 * plain string, and the theme it resolves to, set on <html> as the `light` or `dark` class that
 * picks the palette in globals.css. index.html runs an inline copy of the read-and-apply steps
 * before the bundle loads, so the first paint already has the class.
 */

/** localStorage key for the theme choice. index.html's inline script reads the same key. */
export const THEME_STORAGE_KEY = "loot-dashboard-theme";

/** What the user picks in the sidebar. */
export type ThemeChoice = "light" | "system" | "dark";

/** What a choice resolves to: the class on <html>. */
export type ResolvedTheme = "light" | "dark";

const DARK_QUERY = "(prefers-color-scheme: dark)";

/** Reads a stored or untyped value as a choice; anything else is "system". */
export function parseThemeChoice(value: unknown): ThemeChoice {
  return value === "light" || value === "dark" ? value : "system";
}

/** The OS appearance, or dark when the browser cannot say (the inline script's fallback too). */
function systemTheme(): ResolvedTheme {
  try {
    return window.matchMedia(DARK_QUERY).matches ? "dark" : "light";
  } catch {
    return "dark";
  }
}

/** Resolves a choice to light or dark, asking the OS for "system". */
export function resolveTheme(choice: ThemeChoice): ResolvedTheme {
  return choice === "system" ? systemTheme() : choice;
}

/**
 * Puts exactly one of `light` / `dark` on <html>. Does nothing while the Breaks Studio panel
 * designer is open (`pd-app` on <html>): it is dark-only, and puts the theme back when it closes
 * (`usePanelsDocumentClass`).
 */
export function applyResolvedTheme(resolved: ResolvedTheme): void {
  const classes = document.documentElement.classList;
  if (classes.contains("pd-app")) return;
  classes.remove("light", "dark");
  classes.add(resolved);
}

function readStoredChoice(): ThemeChoice {
  try {
    return parseThemeChoice(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return "system";
  }
}

interface ThemeState {
  /** The user's choice. */
  choice: ThemeChoice;
  /** The theme on <html> now. */
  resolved: ResolvedTheme;
  /** Saves the choice (in memory only when localStorage refuses) and applies it. */
  setChoice: (choice: ThemeChoice) => void;
}

/** The theme store. Module scope, like the scope store, so no provider is needed. */
export const useThemeStore = create<ThemeState>()((set) => ({
  choice: "system",
  resolved: "dark",
  setChoice: (choice) => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, choice);
    } catch {
      // Private mode or a full quota: the choice lasts until the tab closes.
    }
    const resolved = resolveTheme(choice);
    applyResolvedTheme(resolved);
    set({ choice, resolved });
  },
}));

/**
 * Loads the stored choice, applies it, and follows the OS appearance while the choice is
 * "system". Call once, before the first render.
 */
export function initTheme(): void {
  const choice = readStoredChoice();
  const resolved = resolveTheme(choice);
  applyResolvedTheme(resolved);
  useThemeStore.setState({ choice, resolved });
  try {
    window.matchMedia(DARK_QUERY).addEventListener("change", () => {
      if (useThemeStore.getState().choice !== "system") return;
      const next = systemTheme();
      applyResolvedTheme(next);
      useThemeStore.setState({ resolved: next });
    });
  } catch {
    // No matchMedia: "system" stays on the theme it resolved to at load.
  }
  // Browsers drop background colours when printing, so dark-theme text would print light grey on
  // white paper (Packing's Cards View is printed). Print in the light palette, then restore.
  window.addEventListener("beforeprint", () => applyResolvedTheme("light"));
  window.addEventListener("afterprint", () =>
    applyResolvedTheme(useThemeStore.getState().resolved),
  );
}
